import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './RideForm.module.css';
import { useAppContext } from '@/context/AppContext';

interface RideFormProps {
  visible: boolean;
  onSubmit: (rideId: string, rideDetails?: any) => void;
}

const RideForm: React.FC<RideFormProps> = ({ visible, onSubmit }) => {
  const [rideId, setRideId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { getSourceBackendUrl, apiKey } = useAppContext();
  
  // Focus the input when the component mounts
  useEffect(() => {
    if (visible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [visible]);
  
  if (!visible) return null;
  
  const fetchVehicleRideDetails = async (id: string) => {
    setIsLoading(true);
    setError('');
    
    try {
      const backendUrl = getSourceBackendUrl();
      
      // First validate the connection
      console.log('Validating connection before fetching vehicle ride details');
      const validationUrl = `${backendUrl}/api/tools/tracking-sim/validate-connection/`;
      
      const validationResponse = await fetch(validationUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-tracking-sim-api-key': apiKey || ''
        },
        mode: 'cors'
      });
      
      if (!validationResponse.ok) {
        const errorData = await validationResponse.json();
        throw new Error(errorData.error || 'Connection validation failed');
      }
      
      // After successful validation, fetch vehicle ride details
      console.log('Connection validated, fetching vehicle ride details');
      const url = `${backendUrl}/api/tools/tracking-sim/fetch-vehicle-ride-details/?vehicle_ride_id=${id}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-tracking-sim-api-key': apiKey || ''
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch vehicle ride details');
      }
      
      const data = await response.json();
      onSubmit(id, data);
      
      // Navigate to the vehicle ride page
      navigate(`/vehicle-ride/${id}/`);
    } catch (err: any) {
      console.error('Error in vehicle ride workflow:', err);
      setError(err.message || 'Failed to process vehicle ride request');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (rideId.trim()) {
      fetchVehicleRideDetails(rideId);
    }
  };
  
  return (
    <div className={styles.formContainer}>
      <h2 className={styles.formTitle}>Enter Vehicle Ride ID</h2>
      
      {error && <div className={styles.errorMessage}>{error}</div>}
      
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.inputGroup}>
          <input
            type="text"
            id="rideId"
            className={styles.input}
            value={rideId}
            onChange={(e) => setRideId(e.target.value)}
            placeholder="Vehicle Ride ID"
            required
            ref={inputRef}
            disabled={isLoading}
          />
        </div>
        
        <button type="submit" className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`} disabled={isLoading}>
          {isLoading ? (
            <span className={styles.buttonText}>Loading...</span>
          ) : (
            <>
              <span className={styles.buttonText}>Continue</span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.buttonIcon}>
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default RideForm;
